import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  type ReactNode,
  type RefObject,
} from "react";

type FilterValue = string | string[] | undefined;

type FilterValues = Record<string, FilterValue>;

interface FilterDefinition {
  id: string;
  label: string;
}

interface FiltersContextValue {
  filters: FilterDefinition[];
  values: FilterValues;
  visibleFilters: string[];
  hasActiveFilters: boolean;
  searchRef: RefObject<HTMLInputElement | null>;
  getValue: (id: string) => FilterValue;
  setValue: (id: string, value: FilterValue) => void;
  setFilterVisible: (id: string, visible: boolean) => void;
  clearAll: () => void;
}

interface FiltersProviderProps {
  /** Omitted by a surface whose row is a search field and nothing else. */
  filters?: FilterDefinition[];
  values: FilterValues;
  onValuesChange: (values: FilterValues) => void;
  visibleFilters?: string[];
  onVisibleFiltersChange?: (visibleFilters: string[]) => void;
  children: ReactNode;
}

const FiltersContext = createContext<FiltersContextValue | null>(null);

const NO_FILTERS: FilterDefinition[] = [];
const NO_VISIBLE_FILTERS: string[] = [];

function isFilterValueSet(value: FilterValue) {
  return Array.isArray(value) ? value.length > 0 : Boolean(value);
}

function FiltersProvider({
  filters = NO_FILTERS,
  values,
  onValuesChange,
  visibleFilters = NO_VISIBLE_FILTERS,
  onVisibleFiltersChange,
  children,
}: FiltersProviderProps) {
  const searchRef = useRef<HTMLInputElement>(null);

  const getValue = useCallback((id: string) => values[id], [values]);

  const setValue = useCallback(
    (id: string, value: FilterValue) => {
      onValuesChange({ ...values, [id]: value });
    },
    [onValuesChange, values]
  );

  const setFilterVisible = useCallback(
    (id: string, visible: boolean) => {
      onVisibleFiltersChange?.(
        visible
          ? [...visibleFilters, id]
          : visibleFilters.filter((item) => item !== id)
      );

      if (!visible && isFilterValueSet(values[id])) {
        onValuesChange({ ...values, [id]: undefined });
      }
    },
    [onValuesChange, onVisibleFiltersChange, values, visibleFilters]
  );

  const clearAll = useCallback(() => {
    onValuesChange(
      Object.fromEntries(Object.keys(values).map((id) => [id, undefined]))
    );
    searchRef.current?.focus();
  }, [onValuesChange, values]);

  const hasActiveFilters = Object.values(values).some(isFilterValueSet);

  // A filter can carry a value without ever having gone through
  // `setFilterVisible` (e.g. one set from a shared URL), so a stored
  // preference alone can't be allowed to hide an active filter.
  const effectiveVisibleFilters = useMemo(() => {
    const forcedVisible = filters
      .map((filter) => filter.id)
      .filter(
        (id) => !visibleFilters.includes(id) && isFilterValueSet(values[id])
      );

    return forcedVisible.length > 0
      ? [...visibleFilters, ...forcedVisible]
      : visibleFilters;
  }, [filters, values, visibleFilters]);

  const contextValue = useMemo(
    () => ({
      filters,
      values,
      visibleFilters: effectiveVisibleFilters,
      hasActiveFilters,
      searchRef,
      getValue,
      setValue,
      setFilterVisible,
      clearAll,
    }),
    [
      clearAll,
      effectiveVisibleFilters,
      filters,
      getValue,
      hasActiveFilters,
      setFilterVisible,
      setValue,
      values,
    ]
  );

  return (
    <FiltersContext.Provider value={contextValue}>
      {children}
    </FiltersContext.Provider>
  );
}

function useFilters() {
  const context = useContext(FiltersContext);

  if (!context) {
    throw new Error("useFilters must be used within FiltersProvider");
  }

  return context;
}

export { FiltersProvider, isFilterValueSet, useFilters };
export type {
  FilterDefinition,
  FiltersProviderProps,
  FilterValue,
  FilterValues,
};
